import {
  ArrowLeft,
  Award,
  ClipboardList,
  Clock,
  CloudUpload,
  Grid3x3,
  Medal,
  MousePointerClick,
  Network,
  Shuffle,
  Trophy,
  Users,
} from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useSelectedClub } from '@/features/club';
import {
  BEST_OF_LABELS,
  Bracket,
  COMPETITION_CATEGORY_LABELS,
  COMPETITION_SYSTEM_LABELS,
  drawKindOf,
  DrawSuspenseOverlay,
  GroupCard,
  nextPowerOfTwo,
  placeholderBracketRounds,
  playingParticipants,
  plannedGroupCount,
  roundRobinPlan,
  SECOND_PHASE_LABELS,
  StepList,
  useCompetitionQuery,
  useDrawMutation,
  useDrawSuspense,
  useParticipantsQuery,
  WinnerCard,
  type DrawKind,
} from '@/features/competition';
import { ApiError } from '@/shared/api/ApiError';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  PageHeader,
  RadioCard,
  Skeleton,
  useToast,
} from '@/shared/ui';

/** Sposób losowania z makiety — API wspiera losowanie z opcjonalnymi seedami. */
type DrawMethod = 'random' | 'manual' | 'ranking' | 'import';

/** Klucze tłumaczeń zależne od rodzaju losowania. */
const KIND_KEY: Record<Exclude<DrawKind, 'none'>, 'bracket' | 'groups' | 'roundRobin'> = {
  bracket: 'bracket',
  groups: 'groups',
  'round-robin': 'roundRobin',
};

/** Pojedynczy wiersz panelu podsumowania: ikona, etykieta, wartość. */
function SummaryRow({ icon, label, value }: { icon: ReactNode; label: string; value: ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-fg-subtle mt-0.5 shrink-0 [&_svg]:size-4">{icon}</span>
      <div className="min-w-0">
        <dt className="text-fg-muted text-xs">{label}</dt>
        <dd className="text-fg-strong text-sm font-semibold">{value}</dd>
      </div>
    </div>
  );
}

/**
 * Zakładka „Losowanie" — układ z makiety `losowanie.png`: wybór sposobu
 * losowania, podgląd struktury i przycisk „Losuj…" (`POST /{id}/draw` —
 * backend sam wybiera algorytm po systemie rozgrywek i przenosi turniej
 * w IN_PROGRESS). Po losowaniu zakładka przekierowuje do widoku przebiegu
 * (drabinka / grupy / tabela).
 */
export function TournamentDrawPage() {
  const { t } = useTranslation('tournament');
  const { competitionId = '' } = useParams<{ competitionId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { clubId } = useSelectedClub();

  const competitionQuery = useCompetitionQuery(competitionId);
  const participantsQuery = useParticipantsQuery(competitionId);
  const drawMutation = useDrawMutation(competitionId, clubId);

  const [method, setMethod] = useState<DrawMethod>('random');

  /** Inscenizacja „Losowanie w toku” — wspólna z losowaniem fazy finałowej. */
  const drawSuspense = useDrawSuspense();

  const competition = competitionQuery.data;
  const participants = useMemo(() => participantsQuery.data ?? [], [participantsQuery.data]);

  const kind = competition ? drawKindOf(competition.system) : 'none';

  /**
   * Zgłoszenia, które faktycznie wejdą do losowania — bez rezerwy.
   *
   * Podgląd musi liczyć dokładnie to, co policzy backend: przy 48 zgłoszeniach
   * i limicie 32 losowanie widzi 32 zawodników, więc obiecywanie drabinki na 64
   * miejsca z 16 wolnymi losami było zwykłym kłamstwem.
   */
  const playing = useMemo(
    () => playingParticipants(participants, competition?.maxPlayers ?? 0),
    [participants, competition],
  );
  const occupied = playing.length;
  // Rozstawienie zawodnika z rezerwy nie ma znaczenia — nie wejdzie do drabinki.
  const seededCount = useMemo(
    () => playing.filter((item) => item.seed !== null).length,
    [playing],
  );

  /** Komunikat błędu z API albo tekst zastępczy. */
  function describe(error: unknown, fallback: string): string {
    return error instanceof ApiError ? error.message : fallback;
  }

  function handleDraw() {
    drawSuspense.begin();

    drawMutation.mutate(undefined, {
      onSuccess: () =>
        // Wynik pokazujemy dopiero po dograniu paska do końca — API kończy
        // w ułamku sekundy, ale losowanie ma mieć swój moment.
        drawSuspense.finishAfterMinimum(() => {
          showToast({ tone: 'success', description: t('draw.toast.drawn') });
          // Wynik losowania mieszka w zakładce przebiegu.
          void navigate(`/turnieje/${competitionId}/drabinka`);
        }),
      onError: (error) => {
        drawSuspense.abort();
        showToast({ tone: 'danger', description: describe(error, t('draw.toast.drawFailed')) });
      },
    });
  }

  /** Podpis końcówki paska — zależny od tego, co się losuje. */
  const finalCaption =
    kind === 'groups'
      ? t('draw.progress.finalizeGroups')
      : kind === 'round-robin'
        ? t('draw.progress.finalizeSchedule')
        : t('draw.progress.finalizeBracket');

  if (competitionQuery.isError) {
    return <Alert tone="danger">{t('draw.loadError')}</Alert>;
  }

  if (!competition || participantsQuery.isPending) {
    return <Skeleton className="h-96 w-full" />;
  }

  // Po losowaniu (i po zakończeniu) właściwym widokiem jest zakładka przebiegu.
  // UWAGA: w trakcie inscenizacji „Losowanie w toku” status w tle zdążył już
  // przejść na IN_PROGRESS — wtedy NIE przekierowujemy od razu, tylko czekamy,
  // aż pasek dojedzie do końca (przejściem steruje odroczony timer w handleDraw).
  if (
    (competition.status === 'IN_PROGRESS' || competition.status === 'COMPLETED') &&
    !drawSuspense.active
  ) {
    return <Navigate to={`/turnieje/${competitionId}/drabinka`} replace />;
  }

  const kindKey = kind === 'none' ? 'bracket' : KIND_KEY[kind];
  const secondPhaseLabel = competition.secondPhaseType
    ? SECOND_PHASE_LABELS[competition.secondPhaseType]
    : SECOND_PHASE_LABELS.PLAYOFF;

  /* --- Stany, w których losowanie nie jest dostępne --- */

  const blockedAlert =
    kind === 'none' ? (
      <Alert tone="info">{t('draw.state.challenge')}</Alert>
    ) : competition.status === 'CANCELLED' ? (
      <Alert tone="warning">{t('draw.state.cancelled')}</Alert>
    ) : competition.status === 'DRAFT' || competition.status === 'AWAITING_PAYMENT' ? (
      <Alert tone="info">
        <span className="flex flex-wrap items-center gap-3">
          {t('draw.state.beforeRegistration')}
          <Button size="sm" onClick={() => void navigate(`/turnieje/${competitionId}/zawodnicy`)}>
            {t('draw.state.goToParticipants')}
          </Button>
        </span>
      </Alert>
    ) : competition.status === 'REGISTRATION_OPEN' ? (
      <Alert tone="info">
        <span className="flex flex-wrap items-center gap-3">
          {t('draw.state.registrationOpen')}
          <Button size="sm" onClick={() => void navigate(`/turnieje/${competitionId}/zawodnicy`)}>
            {t('draw.state.goToParticipants')}
          </Button>
        </span>
      </Alert>
    ) : null;

  if (blockedAlert) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader title={t(`draw.title.${kindKey}`)} />
        {blockedAlert}
      </div>
    );
  }

  /* --- Widok przed losowaniem (REGISTRATION_CLOSED) --- */

  const bracketSize = nextPowerOfTwo(Math.max(occupied, 2));
  const byes = bracketSize - occupied;
  const groupSize = competition.groupSize ?? 4;
  const groupCount = plannedGroupCount(occupied, groupSize);
  const rrPlan = roundRobinPlan(occupied);
  const tooFew = occupied < 2;

  const methodInfo =
    method === 'manual'
      ? kind === 'bracket'
        ? t('draw.method.manualInfoBracket')
        : t('draw.method.manualInfoGroups')
      : t('draw.method.randomInfo');

  const steps = [
    {
      label: t(
        `draw.steps.generate${kind === 'bracket' ? 'Bracket' : kind === 'groups' ? 'Groups' : 'Schedule'}`,
      ),
      state: 'current' as const,
    },
    ...(kind === 'groups'
      ? [{ label: t('draw.steps.finalPhase', { phase: secondPhaseLabel }), state: 'todo' as const }]
      : []),
    { label: t('draw.steps.manage'), state: 'todo' as const },
  ];

  /** Rozmiary grup w podglądzie — wyrównany podział jak w backendzie. */
  function previewGroupSlots(index: number): number {
    const base = Math.floor(occupied / groupCount);
    const remainder = occupied % groupCount;
    return base + (index < remainder ? 1 : 0);
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t(`draw.title.${kindKey}`)}
        description={t(`draw.description.${kindKey}`)}
      />

      <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_19rem]">
        <div className="flex flex-col gap-5">
          {/* Sposób losowania */}
          <Card padding="lg" className="flex flex-col gap-4">
            <CardHeader title={t('draw.method.legend')} className="mb-0" />

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <RadioCard
                name="draw-method"
                value="random"
                checked={method === 'random'}
                onSelect={() => setMethod('random')}
                icon={<Shuffle />}
                title={t('draw.method.random')}
                description={t('draw.method.randomHint')}
              />
              <RadioCard
                name="draw-method"
                value="manual"
                checked={method === 'manual'}
                onSelect={() => setMethod('manual')}
                icon={<MousePointerClick />}
                title={t('draw.method.manual')}
                description={t('draw.method.manualHint')}
              />
              <RadioCard
                name="draw-method"
                value="ranking"
                checked={false}
                onSelect={() => undefined}
                disabled
                icon={<Trophy />}
                title={t('draw.method.ranking')}
                description={t('draw.method.rankingHint')}
              />
              <RadioCard
                name="draw-method"
                value="import"
                checked={false}
                onSelect={() => undefined}
                disabled
                icon={<CloudUpload />}
                title={t('draw.method.import')}
                description={t('draw.method.importHint')}
              />
            </div>

            <Alert tone="info">
              <span className="flex flex-wrap items-center gap-3">
                <span>
                  {methodInfo}
                  {method === 'random' &&
                    seededCount > 0 &&
                    ` ${t('draw.method.randomInfoSeeded', { count: seededCount })}`}
                  {method === 'manual' &&
                    ` ${
                      seededCount > 0
                        ? t('draw.method.manualSeeded', { count: seededCount })
                        : t('draw.method.manualNone')
                    }`}
                </span>
                {method === 'manual' && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => void navigate(`/turnieje/${competitionId}/zawodnicy`)}
                  >
                    {t('draw.method.editSeeds')}
                  </Button>
                )}
              </span>
            </Alert>
          </Card>

          {/* Podgląd struktury */}
          <Card padding="lg" className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardHeader title={t(`draw.preview.${kindKey}`)} className="mb-0" />
              <span className="text-fg-muted text-xs">
                {kind === 'bracket' &&
                  `${t('draw.preview.bracketInfo', { count: occupied, size: bracketSize })}${
                    byes > 0 ? ` • ${t('draw.preview.bracketByes', { byes })}` : ''
                  }`}
                {kind === 'groups' &&
                  t('draw.preview.groupsInfo', {
                    count: occupied,
                    groups: groupCount,
                    size: groupSize,
                  })}
                {kind === 'round-robin' &&
                  t('draw.preview.roundRobinInfo', {
                    count: occupied,
                    rounds: rrPlan.rounds,
                    matches: rrPlan.matches,
                  })}
              </span>
            </div>

            {tooFew && <Alert tone="warning">{t('draw.preview.tooFew')}</Alert>}

            {!tooFew && kind === 'bracket' && (
              <Bracket
                rounds={placeholderBracketRounds(occupied)}
                finaleCompact
                finale={
                  <WinnerCard title={t('draw.result.champion')} />
                }
              />
            )}

            {!tooFew && kind === 'groups' && (
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {Array.from({ length: groupCount }, (_, index) => (
                  <GroupCard
                    key={index}
                    title={t('draw.result.group', { number: index + 1 })}
                    placeholderSlots={previewGroupSlots(index)}
                    placeholderLabel={t('draw.preview.groupPlaceholder')}
                  />
                ))}
              </div>
            )}

            {!tooFew && kind === 'round-robin' && (
              <GroupCard
                title={t('draw.result.scheduleTitle')}
                placeholderSlots={Math.min(occupied, 8)}
                placeholderLabel={t('draw.preview.groupPlaceholder')}
              />
            )}
          </Card>
        </div>

        {/* Kolumna prawa — podsumowanie ustawień i następne kroki */}
        <div className="flex flex-col gap-4 xl:sticky xl:top-6">
          <Card padding="lg">
            <CardHeader title={t('draw.summary.title')} />
            <dl className="flex flex-col gap-4">
              <SummaryRow
                icon={<ClipboardList />}
                label={t('draw.summary.name')}
                value={competition.name}
              />
              <SummaryRow
                icon={<Users />}
                label={t('draw.summary.players')}
                value={t('draw.summary.playersValue', {
                  count: occupied,
                  max: competition.maxPlayers,
                })}
              />
              <SummaryRow
                icon={<Award />}
                label={t('draw.summary.category')}
                value={
                  competition.category
                    ? (COMPETITION_CATEGORY_LABELS[competition.category] ?? competition.category)
                    : t('draw.summary.empty')
                }
              />
              <SummaryRow
                icon={<Trophy />}
                label={t('draw.summary.system')}
                value={COMPETITION_SYSTEM_LABELS[competition.system] ?? competition.system}
              />
              {kind === 'groups' && (
                <>
                  <SummaryRow
                    icon={<Grid3x3 />}
                    label={t('draw.summary.groups')}
                    value={t('draw.summary.groupsValue', {
                      size: groupSize,
                      advancing: competition.advancingPerGroup ?? 1,
                    })}
                  />
                  <SummaryRow
                    icon={<Network />}
                    label={t('draw.summary.secondPhase')}
                    value={secondPhaseLabel}
                  />
                </>
              )}
              {(kind === 'bracket' ||
                (kind === 'groups' && competition.secondPhaseType !== 'FINAL_GROUP')) && (
                <SummaryRow
                  icon={<Medal />}
                  label={t('draw.summary.thirdPlace')}
                  value={competition.thirdPlaceMatch ? t('draw.summary.yes') : t('draw.summary.no')}
                />
              )}
              {competition.scoringRules && (
                <SummaryRow
                  icon={<Clock />}
                  label={t('draw.summary.matchFormat')}
                  value={
                    BEST_OF_LABELS[String(competition.scoringRules.bestOf)] ??
                    `Best of ${competition.scoringRules.bestOf}`
                  }
                />
              )}
              <SummaryRow
                icon={<Network />}
                label={t('draw.summary.seeding')}
                value={
                  seededCount > 0
                    ? t('draw.summary.seedingCount', { count: seededCount })
                    : t('draw.summary.seedingNone')
                }
              />
            </dl>
          </Card>

          <Alert tone="warning" title={t('draw.important.title')}>
            {t('draw.important.body')}
          </Alert>

          <Card padding="lg">
            <CardHeader title={t('draw.steps.title')} />
            <StepList steps={steps} />
          </Card>
        </div>
      </div>

      {/* Stopka akcji */}
      <div className="border-border flex items-center justify-between gap-3 border-t pt-5">
        <Button
          variant="secondary"
          size="lg"
          leadingIcon={<ArrowLeft className="size-4" />}
          onClick={() => void navigate(`/turnieje/${competitionId}/zawodnicy`)}
        >
          {t('draw.back')}
        </Button>

        <Button
          size="lg"
          trailingIcon={<Shuffle className="size-4" />}
          disabled={tooFew}
          loading={drawMutation.isPending || drawSuspense.active}
          onClick={handleDraw}
        >
          {t(`draw.submit.${kindKey}`)}
        </Button>
      </div>

      {/* Nakładka „Losowanie w toku” — przykrywa stronę na czas inscenizacji
          i znika dopiero razem z przejściem na drabinkę. */}
      {drawSuspense.active && (
        <DrawSuspenseOverlay progress={drawSuspense.progress} finalCaption={finalCaption} />
      )}
    </div>
  );
}
